<template>
  <div v-if="formDoc" class="form-responses-summary">
    <p v-if="!hasResponses" class="text-muted mb-0">
      {{ $t('settings.forms.noResponses') }}
    </p>
    <div v-else class="summary-grid">
      <div
          v-for="card in cards"
          :key="card.block.id"
          class="summary-card"
      >
        <h4 class="summary-card-title" :title="card.title">{{ card.title }}</h4>

        <!-- NPS: big score + stacked horizontal bar + labeled counts -->
        <template v-if="card.summary.kind === 'nps'">
          <div class="d-flex align-items-baseline">
            <span class="big-number">{{ card.summary.score }}</span>
            <span class="metric-label ml-2">{{ $t('settings.forms.score') }}</span>
          </div>
          <div v-if="card.summary.total > 0" class="nps-chart">
            <bar-chart
                :chart-data="npsChartData(card.summary)"
                :options="npsChartOptions"
                :height="46"
            />
          </div>
          <ul class="nps-legend list-unstyled mb-0">
            <li>
              <span class="dot dot-promoters"></span>
              {{ $t('settings.forms.promoters') }}
              <strong class="ml-auto">{{ card.summary.promoters }}</strong>
            </li>
            <li>
              <span class="dot dot-passives"></span>
              {{ $t('settings.forms.passives') }}
              <strong class="ml-auto">{{ card.summary.passives }}</strong>
            </li>
            <li>
              <span class="dot dot-detractors"></span>
              {{ $t('settings.forms.detractors') }}
              <strong class="ml-auto">{{ card.summary.detractors }}</strong>
            </li>
          </ul>
          <div class="metric-footnote">
            {{ card.summary.total }} {{ $t('settings.forms.answered') }}
          </div>
        </template>

        <!-- Rating: average + per-star distribution bars -->
        <template v-else-if="card.summary.kind === 'average'">
          <div class="d-flex align-items-baseline">
            <span class="big-number">{{ card.summary.average.toFixed(1) }}</span>
            <span class="metric-label ml-2">{{ $t('settings.forms.average') }}</span>
          </div>
          <div v-if="card.summary.distribution" class="bar-rows">
            <div
                v-for="row in ratingRows(card)"
                :key="row.value"
                class="bar-row"
            >
              <span class="bar-label">{{ row.label }}</span>
              <span class="bar-track">
                <span class="bar-fill" :style="{ width: row.width }"></span>
              </span>
              <span class="bar-count">{{ row.count }}</span>
            </div>
          </div>
        </template>

        <!-- Choices: option label + count with proportional bars -->
        <template v-else-if="card.summary.kind === 'choices'">
          <div class="bar-rows">
            <div
                v-for="(option, index) in card.summary.options"
                :key="`${card.block.id}-${index}`"
                class="bar-row"
            >
              <span class="bar-label option-label" :title="option.label">{{ option.label }}</span>
              <span class="bar-track">
                <span
                    class="bar-fill"
                    :style="{ width: choiceWidth(option, card.summary.options) }"
                ></span>
              </span>
              <span class="bar-count">{{ option.count }}</span>
            </div>
          </div>
        </template>

        <!-- Text-ish blocks: answered count only -->
        <template v-else>
          <div class="d-flex align-items-baseline">
            <span class="big-number">{{ card.summary.answered }}</span>
            <span class="metric-label ml-2">{{ $t('settings.forms.answers') }}</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import BarChart from '@/components/settings/forms/BarChart';
import {
  inputBlocksOf,
  summarizeBlock,
  localized,
  variablesOf,
  summarizeVariable,
} from '@/components/settings/forms/formResponses.helpers';

export default {
  name: 'FormResponsesSummary',
  components: {
    BarChart,
  },
  props: {
    formDoc: {
      type: Object,
      default: null,
    },
    responses: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    defaultLanguage() {
      return (this.formDoc && this.formDoc.defaultLanguage) || 'de-DE';
    },
    hasResponses() {
      return Array.isArray(this.responses) && this.responses.length > 0;
    },
    cards() {
      if (!this.formDoc) return [];
      const questionCards = inputBlocksOf(this.formDoc)
        .map((block) => ({
          block,
          title: localized(block.label, this.defaultLanguage, this.defaultLanguage),
          summary: summarizeBlock(block, this.responses),
        }))
        .filter((card) => card.summary.kind !== 'text' || card.summary.answered > 0);
      // Calculated variables after the questions. `block` is a stand-in with
      // type 'variable' so the template and ratingRows (no star labels) work
      // unchanged; a variable without a single stored value has no card.
      const variableCards = variablesOf(this.formDoc)
        .map((variable) => ({
          block: { id: variable.id, type: 'variable' },
          title: variable.name || variable.id,
          summary: summarizeVariable(variable, this.responses),
        }))
        .filter((card) => (card.summary.kind === 'average'
          ? card.summary.total > 0
          : card.summary.options.length > 0));
      return [...questionCards, ...variableCards];
    },
    npsChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        tooltips: {
          enabled: true,
        },
        scales: {
          xAxes: [{
            stacked: true,
            display: false,
          }],
          yAxes: [{
            stacked: true,
            display: false,
            barPercentage: 1,
            categoryPercentage: 1,
          }],
        },
      };
    },
  },
  methods: {
    npsChartData(summary) {
      return {
        labels: [''],
        datasets: [
          {
            label: this.$t('settings.forms.promoters'),
            backgroundColor: '#51d182',
            data: [summary.promoters],
          },
          {
            label: this.$t('settings.forms.passives'),
            backgroundColor: '#b9b9b9',
            data: [summary.passives],
          },
          {
            label: this.$t('settings.forms.detractors'),
            backgroundColor: '#dc5353',
            data: [summary.detractors],
          },
        ],
      };
    },
    ratingRows(card) {
      // Rendering is gated behind v-if="card.summary.distribution", so distribution is always
      // a dense, zero-filled array here (never null/empty) — build rows straight from it instead
      // of re-deriving a 1..max range, which silently broke for sliders (min !== 1 or step !== 1).
      const { distribution } = card.summary;
      if (!distribution) return [];
      const highestCount = distribution.reduce((acc, entry) => Math.max(acc, entry.count), 0);
      const isRating = card.block.type === 'rating';
      return [...distribution].reverse().map((entry) => ({
        value: entry.value,
        count: entry.count,
        label: isRating ? `${entry.value} ★` : `${entry.value}`,
        width: highestCount > 0 ? `${Math.round((entry.count / highestCount) * 100)}%` : '0%',
      }));
    },
    choiceWidth(option, options) {
      const highestCount = options.reduce((acc, entry) => Math.max(acc, entry.count), 0);
      if (highestCount === 0) return '0%';
      return `${Math.round((option.count / highestCount) * 100)}%`;
    },
  },
};
</script>

<style scoped lang="scss">
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.summary-card {
  background-color: #53575b;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
  color: $webmag-light-grey;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.summary-card-title {
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $webmag-light-grey;
  margin-bottom: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.big-number {
  font-size: 2.5rem;
  font-weight: 600;
  line-height: 1;
  color: #fff;
}

.metric-label {
  font-size: 13px;
  text-transform: uppercase;
  color: $webmag-light-grey;
}

.metric-footnote {
  margin-top: 8px;
  font-size: 12px;
  color: $webmag-light-grey;
}

.nps-chart {
  position: relative;
  height: 46px;
  margin-top: 12px;
}

.nps-legend {
  margin-top: 8px;
  font-size: 13px;

  li {
    display: flex;
    align-items: center;
    padding: 2px 0;

    strong {
      color: #fff;
    }
  }
}

.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;

  &.dot-promoters {
    background-color: $webmag-green;
  }

  &.dot-passives {
    background-color: $webmag-light-grey;
  }

  &.dot-detractors {
    background-color: $webmag-red-light;
  }
}

.bar-rows {
  margin-top: 12px;
}

.bar-row {
  display: flex;
  align-items: center;
  padding: 3px 0;
  font-size: 13px;
}

.bar-label {
  flex: 0 0 30%;
  max-width: 30%;
  padding-right: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-track {
  flex: 1 1 auto;
  height: 8px;
  background-color: $webmag-dark-grey;
  overflow: hidden;
}

.bar-fill {
  display: block;
  height: 100%;
  background-color: $webmag-green;
}

.bar-count {
  flex: 0 0 auto;
  min-width: 28px;
  text-align: right;
  color: #fff;
}
</style>
